import { makeAutoObservable, runInAction } from "mobx";
import { toast } from "react-toastify";
import {
  companyListURL,
  companyURL,
  countryListURL,
  deleteCompanyById,
  duplicateCompanyById,
} from "../api/url";
import { COMPANY_FORM_INITIAL } from "../constants/company.constants";
import { authStore } from "../../Auth";
import { SYSTEM_ADMIN } from "../../../constants/constants";
import { handleFetch } from "../../../utils/handleFetch";

class CompanyStore {
  companies = [];
  companyList = [];
  totalCompanies = 0;
  companyForm = COMPANY_FORM_INITIAL;
  duplicateCompanyForm = {
    sourceCompanyId: "",
    companyName: "",
    includeData: false,
  };
  page = 1;
  limit = 12;
  errors = {};
  duplicateErrors = {};
  search = "";
  isCompanyModalOpen = false;
  isDuplicateCompanyModalOpen = false;
  isDeleteConfirmModalOpen = false;
  selectedCompanyId = null;
  selectedDeleteCompanyId = null;
  selectedCompanyName = "";
  isBatchUploadModalOpen = false;
  countryList = [];
  isDuplicateCompanySubmitting = false;

  constructor() {
    makeAutoObservable(this);
  }

  setIsBatchUploadModalOpen = (v) => (this.isBatchUploadModalOpen = v);

  setSelectedCompanyId = (v) => (this.selectedCompanyId = v);

  openCompanyModal = () => (this.isCompanyModalOpen = true);

  closeCompanyModal = () => {
    this.companyForm = COMPANY_FORM_INITIAL;
    this.isCompanyModalOpen = false;
    this.errors = {};
  };

  openDeleteConfirmModal = (companyId, companyName) => {
    this.selectedDeleteCompanyId = companyId;
    this.selectedCompanyName = companyName;
    this.isDeleteConfirmModalOpen = true;
  };

  getSuggestedDuplicateCompanyName = (sourceCompanyName = "") => {
    const normalizedName = sourceCompanyName
      .trim()
      .replace(/\s+\(Copy(?:\s+\d+)?\)$/, "");

    const existingNames = new Set(
      this.companyList.map(({ value }) => value).filter(Boolean),
    );

    let copyNumber = 1;
    let suggestedName = `${normalizedName} (Copy)`;

    while (existingNames.has(suggestedName)) {
      copyNumber += 1;
      suggestedName = `${normalizedName} (Copy ${copyNumber})`;
    }

    return suggestedName;
  };

  openDuplicateCompanyModal = (company) => {
    this.duplicateCompanyForm = {
      sourceCompanyId: company?._id || "",
      companyName: this.getSuggestedDuplicateCompanyName(company?.companyName),
      includeData: false,
    };
    this.duplicateErrors = {};
    this.isDuplicateCompanyModalOpen = true;
  };

  closeDuplicateCompanyModal = () => {
    this.duplicateCompanyForm = {
      sourceCompanyId: "",
      companyName: "",
      includeData: false,
    };
    this.duplicateErrors = {};
    this.isDuplicateCompanyModalOpen = false;
    this.isDuplicateCompanySubmitting = false;
  };

  closeDeleteConfirmModal = () => {
    this.isDeleteConfirmModalOpen = false;
  };

  setSearch = (v) => (this.search = v);

  setCompanyForm = (id) => {
    const company = this.companies.find((c) => c._id === id);
    this.companyForm = { ...company } || COMPANY_FORM_INITIAL;
  };

  setCompanyFormValue = (e) => {
    const { id, value } = e.target;
    this.companyForm[id] = value;
  };

  setDuplicateCompanyFormValue = (e) => {
    const { id, value, type, checked } = e.target;
    this.duplicateCompanyForm[id] = type === "checkbox" ? checked : value;
  };

  setPage = (v) => (this.page = parseInt(v));

  syncCompanyList = (company) => {
    const companyOption = { key: company?._id, value: company?.companyName };
    const companyIndex = this.companyList.findIndex(
      ({ key }) => key === company?._id,
    );

    if (companyIndex > -1) {
      this.companyList[companyIndex] = companyOption;
      return;
    }

    this.companyList.push(companyOption);
  };

  syncCompanyCollection = (company) => {
    const companyIndex = this.companies.findIndex(
      ({ _id }) => _id === company?._id,
    );

    if (companyIndex > -1) {
      this.companies[companyIndex] = company;
    } else {
      if (this.page === 1) {
        if (this.companies.length === this.limit) {
          this.companies.pop();
        }
        this.companies.unshift(company);
      }
      this.totalCompanies += 1;
    }

    this.syncCompanyList(company);
  };

  getCompanies = async () => {
    const url =
      companyURL() +
      `?page=${this.page}&limit=${this.limit}&search=${this.search}`;
    return await handleFetch({
      url,
      method: "GET",
      onSuccess: (data) => {
        runInAction(() => {
          const { total, companies } = data;
          this.totalCompanies = total;
          this.companies = companies;
        });
      },
    });
  };

  getCompanyList = async () => {
    return await handleFetch({
      url: companyListURL,
      method: "GET",
      onSuccess: (data, message) =>
        runInAction(() => (this.companyList = data)),
    });
  };

  saveCompany = async (level = "parent") => {
    const userRole = authStore.role;
    const {
      _id,
      companyName,
      country,
      address,
      businessNo,
      companyType,
      industrySector,
      cLevel,
    } = this.companyForm;
    const body = {
      _id,
      companyName,
      country,
      address,
      businessNo,
      companyType,
      industrySector,
      cLevel,
    };
    const url = _id
      ? companyURL(_id)
      : companyURL(userRole === SYSTEM_ADMIN ? "resellerCom" : level);
    return await handleFetch({
      url,
      method: _id ? "PUT" : "POST",
      body,
      onSuccess: (data, message) => {
        runInAction(() => {
          this.errors = {};
          this.syncCompanyCollection(data);
          this.companyForm = COMPANY_FORM_INITIAL;
        });
        toast.success(message);
      },
      onValidationError: (errors) => runInAction(() => (this.errors = errors)),
    });
  };

  duplicateCompany = async (companyId) => {
    runInAction(() => {
      this.isDuplicateCompanySubmitting = true;
      this.duplicateErrors = {};
    });

    const success = await handleFetch({
      url: duplicateCompanyById(
        companyId || this.duplicateCompanyForm.sourceCompanyId,
      ),
      method: "POST",
      body: {
        companyName: this.duplicateCompanyForm.companyName,
        includeData: this.duplicateCompanyForm.includeData,
      },
      onSuccess: (data, message) => {
        runInAction(() => {
          this.syncCompanyCollection(data);
          this.closeDuplicateCompanyModal();
        });
        toast.success(message);
      },
      onValidationError: (errors) =>
        runInAction(() => (this.duplicateErrors = errors)),
    });

    runInAction(() => {
      this.isDuplicateCompanySubmitting = false;
    });

    return success;
  };

  handleDeleteCompany = async () => {
    return await handleFetch({
      url: deleteCompanyById(this.selectedDeleteCompanyId),
      method: "PATCH",
      onSuccess: (_, message) => {
        runInAction(() => {
          this.isDeleteConfirmModalOpen = false;
          this.getCompanies();
          this.selectedDeleteCompanyId = null;
        });
        toast.success(message);
      },
    });
  };

  getCountryList = async () => {
    return await handleFetch({
      url: countryListURL,
      method: "GET",
      onSuccess: (data) => runInAction(() => (this.countryList = data)),
    });
  };
}

export const companyStore = new CompanyStore();
